import React, { FormEvent, memo, useEffect } from 'react';
import { get } from 'lodash';
import DropZone from 'react-dropzone';

import styles from './WifiForm.module.css';
import updateCharacter from '../../assets/images/update-character.png';
import {
  wifiDoneLabel,
  wifiFormInvalidFileFormatWarning,
  wifiPageRemoveButtonLabel,
  supportedExtensionsListMessage,
  wifiFormDuplicateFilenameWarning,
  wifiFormInvalidDueToMissingFiles,
  wifiFormMissingRequiredFileField,
  wifiFormMustBePEM,
  wifiFormNeedsSingleFile,
  wifiFormFileReadError,
  wifiFormFileUploadInstruction,
} from '../../strings';
import AddIcon from '../addIcon/AddIcon';
import Input from '../atoms/input/Input';
import PasswordInput from '../atoms/passwordInput/PasswordInput';
import PrimaryButton from '../primaryButton/PrimaryButton';
import DropdownSelect from '../atoms/dropdownSelect/DropdownSelect';
import Checkbox from '../atoms/checkbox/Checkbox';
import { deobfuscate } from '../../utils/obfuscation';
import usePrevious from '../../hooks/usePrevious';
import { TickIcon } from '../icons';
import { supportedCertificateFormats } from './constants';
import { FileWithContent, readFile } from '../../utils/readFile';

enum WifiFormPreset {
  BASIC = 'BASIC',
  LAUSD = 'LAUSD',
  CUSTOM = 'CUSTOM',
}

const wifiFormPresetDisplayNames: Record<WifiFormPreset, string> = {
  [WifiFormPreset.BASIC]: 'Basic (WPA Personal)',
  [WifiFormPreset.LAUSD]: 'LAUSD',
  [WifiFormPreset.CUSTOM]: 'Custom',
};

export enum NetworkType {
  WPA_PERSONAL = 'WPA_PERSONAL',
  WPA_ENTERPRISE = 'WPA_ENTERPRISE',
  OPEN = 'OPEN',
  OWE = 'OWE',
  LEAP = 'LEAP',
}

const networkTypeDisplayNames: Record<NetworkType, string> = {
  [NetworkType.WPA_PERSONAL]: 'WPA Personal',
  [NetworkType.WPA_ENTERPRISE]: 'WPA Enterprise',
  [NetworkType.OPEN]: 'Open',
  [NetworkType.OWE]: 'OWE',
  [NetworkType.LEAP]: 'LEAP',
};

enum EnterpriseAuthenticationType {
  PWD = 'PWD',
  TLS = 'TLS',
  TTLS = 'TTLS',
  PEAP = 'PEAP',
}

enum InnerAuthenticationType {
  MSCHAPv2 = 'MSCHAPv2',
  MD5 = 'MD5',
  GTC = 'GTC',
}

enum PeapVersion {
  AUTOMATIC = 'AUTOMATIC',
  V0 = 'V0',
  V1 = 'V1',
}

enum FieldMode {
  REQUIRED = 'REQUIRED',
  OPTIONAL = 'OPTIONAL',
}

export const isUploadField = (fieldName: string) =>
  [
    'authentication.data.ca_cert',
    'authentication.data.user_cert',
    'authentication.data.user_private_key',
    'authentication.data.additional_ca_certs',
  ].includes(fieldName);

const supportedFormats = supportedCertificateFormats;
const supportedFormatsWithDots = supportedFormats.map((format) => `.${format}`);

const isFileFormatSupported = (filename: string) => {
  return supportedFormatsWithDots.some((format) => filename.endsWith(format));
};

export const networkFields: { [key in NetworkType]: Record<any, any> } = {
  [NetworkType.OPEN]: {
    ssid: FieldMode.REQUIRED,
    hidden: FieldMode.OPTIONAL,
    'authentication.data.additional_ca_certs': FieldMode.OPTIONAL,
  },
  [NetworkType.OWE]: {
    ssid: FieldMode.REQUIRED,
    hidden: FieldMode.OPTIONAL,
    'authentication.data.additional_ca_certs': FieldMode.OPTIONAL,
  },
  [NetworkType.WPA_PERSONAL]: {
    ssid: FieldMode.REQUIRED,
    hidden: FieldMode.OPTIONAL,
    'authentication.data.password': FieldMode.REQUIRED,
    'authentication.data.additional_ca_certs': FieldMode.OPTIONAL,
  },
  [NetworkType.LEAP]: {
    ssid: FieldMode.REQUIRED,
    hidden: FieldMode.OPTIONAL,
    'authentication.data.username': FieldMode.REQUIRED,
    'authentication.data.password': FieldMode.REQUIRED,
    'authentication.data.additional_ca_certs': FieldMode.OPTIONAL,
  },
  [NetworkType.WPA_ENTERPRISE]: {
    [EnterpriseAuthenticationType.PWD]: {
      ssid: FieldMode.REQUIRED,
      hidden: FieldMode.OPTIONAL,
      'authentication.data.authentication': FieldMode.REQUIRED,
      'authentication.data.username': FieldMode.REQUIRED,
      'authentication.data.password': FieldMode.REQUIRED,
      'authentication.data.additional_ca_certs': FieldMode.OPTIONAL,
    },
    [EnterpriseAuthenticationType.TLS]: {
      ssid: FieldMode.REQUIRED,
      hidden: FieldMode.OPTIONAL,
      'authentication.data.authentication': FieldMode.REQUIRED,
      'authentication.data.identity': FieldMode.REQUIRED,
      'authentication.data.domain': FieldMode.OPTIONAL,
      'authentication.data.ca_cert': FieldMode.OPTIONAL,
      'authentication.data.ca_cert_password': FieldMode.OPTIONAL,
      'authentication.data.user_cert': FieldMode.REQUIRED,
      'authentication.data.user_cert_password': FieldMode.OPTIONAL,
      'authentication.data.user_private_key': FieldMode.REQUIRED,
      'authentication.data.user_private_key_password': FieldMode.OPTIONAL,
      'authentication.data.additional_ca_certs': FieldMode.OPTIONAL,
    },
    [EnterpriseAuthenticationType.TTLS]: {
      ssid: FieldMode.REQUIRED,
      hidden: FieldMode.OPTIONAL,
      'authentication.data.authentication': FieldMode.REQUIRED,
      'authentication.data.username': FieldMode.REQUIRED,
      'authentication.data.password': FieldMode.OPTIONAL,
      'authentication.data.inner_authentication': FieldMode.REQUIRED,
      'authentication.data.anonymous_identity': FieldMode.OPTIONAL,
      'authentication.data.ca_cert': FieldMode.OPTIONAL,
      'authentication.data.ca_cert_password': FieldMode.OPTIONAL,
      'authentication.data.additional_ca_certs': FieldMode.OPTIONAL,
    },
    [EnterpriseAuthenticationType.PEAP]: {
      ssid: FieldMode.REQUIRED,
      hidden: FieldMode.OPTIONAL,
      'authentication.data.authentication': FieldMode.REQUIRED,
      'authentication.data.username': FieldMode.REQUIRED,
      'authentication.data.password': FieldMode.OPTIONAL,
      'authentication.data.inner_authentication': FieldMode.REQUIRED,
      'authentication.data.peap_version': FieldMode.REQUIRED,
      'authentication.data.anonymous_identity': FieldMode.OPTIONAL,
      'authentication.data.domain': FieldMode.OPTIONAL,
      'authentication.data.ca_cert': FieldMode.OPTIONAL,
      'authentication.data.ca_cert_password': FieldMode.OPTIONAL,
      'authentication.data.additional_ca_certs': FieldMode.OPTIONAL,
    },
  },
};

export type JSONFile = {
  filename: string;
  folder: string;
  content: string;
};

type AuthenticationData = {
  authentication: EnterpriseAuthenticationType;
  password: string;
  username: string;
  inner_authentication: InnerAuthenticationType;
  peap_version: PeapVersion;
  anonymous_identity: string;
  domain: string;
  ca_cert: JSONFile[];
  user_cert: JSONFile[];
  user_private_key: JSONFile[];
  ca_cert_password: string;
  user_cert_password: string;
  user_private_key_password: string;
  identity: string;
  additional_ca_certs: JSONFile[];
};

export type WifiFormData = {
  preset: WifiFormPreset;
  presetLAUSDPassword: boolean;
  authentication: {
    type: NetworkType;
    data: AuthenticationData;
  };
  ssid: string;
  hidden: boolean;
};

const defaultAuthenticationData: AuthenticationData = {
  authentication: EnterpriseAuthenticationType.PEAP,
  password: '',
  username: '',
  inner_authentication: InnerAuthenticationType.MSCHAPv2,
  peap_version: PeapVersion.AUTOMATIC,
  anonymous_identity: '',
  domain: '',
  ca_cert: [], // Single file
  user_cert: [], // Single file
  user_private_key: [], // Single file
  ca_cert_password: '',
  user_cert_password: '',
  user_private_key_password: '',
  identity: '',
  additional_ca_certs: [], // Multiple files
};

// LAUSD specific strings
export const lausdReminderToCheckNetworkName =
  'Please check and adjust this network name if your school’s network has a different capitalization or spelling';
export const lausdPresetUsername = 'pi-top';
export const lausdPresetPassword = deobfuscate('0FzSAN3YxQHMiBjUGJGM');

export const lausdPresetCertificates = [
  {
    filename: 'lausd-root-ca2.pem',
    folder: '',
    content: `-----BEGIN CERTIFICATE-----
MIIFdjCCA16gAwIBAgIQEpQXS4+cz7RAurGQEtPH6zANBgkqhkiG9w0BAQsFADBD
MRMwEQYKCZImiZPyLGQBGRYDbmV0MRUwEwYKCZImiZPyLGQBGRYFbGF1c2QxFTAT
BgNVBAMTDExBVVNEIFJvb3RDQTAeFw0xNTEyMjgxODU1MDZaFw0zNzEyMjgxOTA1
MDRaMEMxEzARBgoJkiaJk/IsZAEZFgNuZXQxFTATBgoJkiaJk/IsZAEZFgVsYXVz
ZDEVMBMGA1UEAxMMTEFVU0QgUm9vdENBMIICIjANBgkqhkiG9w0BAQEFAAOCAg8A
MIICCgKCAgEA5D/Qpa7HkEtsEM2jaR0254saWUlDcO6DC2tj3WR/OAkVmkdBWMsS
zFev/geCgr4obweoqLlrE13EvrtK1ETaRtQTHLWBJKOVkuvI68yWQ5yQzY607Rww
LK/m56/OhrVGTpScCcZQKUCOdy56ACqPcTRYT8D/Ym/Oa2UB7yRrAzxOSnpzXQ/q
vcmSYVgR7SCXTKaWHLKsSj2WiXMKejwKD9Ul5l0OLkXDTuOR8tuqzlxorkLmYy1O
BUFncV+AOvrtfV4ARHNtZnm+gt6IAFOzhvivPIEYXUtk3S8Wmq634mL3qTX++KYf
7dVx8bODGkFzEa3QOXa1awE7oQ/UzAts1zxFJJNziE7XOTjuTlkRUFmMjI8rw0LJ
fDpOtq5OC9lZtjO49uLhtX5cfePTZ0OapS87MgTR8XSca75qmyoOs15GZR74qM0E
iNjZTqzBYTRkVd6+ewWpuxqaPMf22rrpg/ybGWBUS2m5cRqbGbcA1m3bMsFpcf5U
XMKxqafvHnr3sttgi3WKggPpOv8oJ39Co2XDNoEU00JmarwA4YccrwVOGm+rcMI7
7L6yiL5RxMJojIQtI4kml89OhQCBhk9/dyO/0Dr6XlemxRzso6jJvTV0r8b46eDA
RBTe073VPuhYXn/tXJRlTFc4mrVNu90yje/EN+3pRoHJljB91n76+p8CAwEAAaNm
MGQwEwYJKwYBBAGCNxQCBAYeBABDAEEwCwYDVR0PBAQDAgGGMA8GA1UdEwEB/wQF
MAMBAf8wHQYDVR0OBBYEFFzb3LdrxDQEbJ5AJACWWcT9dVa6MBAGCSsGAQQBgjcV
AQQDAgEAMA0GCSqGSIb3DQEBCwUAA4ICAQAdl9dQQ9a6LdddD89/cbcRA6mp2kCY
NtJPHlsyN0nllxC9IC6U5y6QvdXbBVkTxgiDJ+EbrPohbKzjWx2u9xyxKPMy5jTW
rNctYe9MFSmvhDmQ3jGAUTQegCT89DtP4hjIYBH8E2YLwggbKOs1RYFxds4oFyKs
BSVLwgtLSdkxxRw7AmDNTG2/+PNKTEnnhAgT1bBAF1XwqsNgwdRk5gL3qWzHWkP1
J0ERpGpcA/rk4pTBc70AJ/c1TwIJsuTmzZ1z/pu0MVmvjqb1wfAwEGRLcUqUrAr4
oQR+Z3u9XWFmvyqGSg+7TyBUEaXp6tN3m5kE8+I5Hf4lB1wswxNGs6cgEzM7pDdb
Y1NsmUPQIwQaR3PmInwU88KHVyjLzHiCAxuDg1yY2SaLeqILtFT9ee/Z+8xffiCi
GZvx+78uYj1SUKMl5NepHU3WI5FtkB56Jw4DVQt9rWUasr/+X/pHErVqCwRULXT4
AYEI+q8a5MWU/T9SsgwWU3GIvo66A5NTqzu7V8CDfmSi8rJsn/dC+jhTMw/84iri
3eUaK3zHBYVDCN7ZLiazrmKozKkLsWYVT29wNtXvA/vRQMT5Jh4f55DihXEXTMDZ
RYN467h3SPsE1bVPx2pZjQfRV7RvVN3+UbFUW+DRAkAStiTaM2eWe5i7mXCGtnNk
/qwhPd/L29leoA==
-----END CERTIFICATE-----
`,
  },
  {
    filename: 'lausd-sub-ca2.pem',
    folder: '',
    content: `-----BEGIN CERTIFICATE-----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-----END CERTIFICATE-----
`,
  },
];

const presets: Record<WifiFormPreset, WifiFormData> = {
  BASIC: {
    preset: WifiFormPreset.BASIC,
    presetLAUSDPassword: false,
    authentication: {
      type: NetworkType.WPA_PERSONAL,
      data: defaultAuthenticationData,
    },
    ssid: '',
    hidden: false,
  },
  LAUSD: {
    preset: WifiFormPreset.LAUSD,
    presetLAUSDPassword: false,
    authentication: {
      type: NetworkType.WPA_ENTERPRISE,
      data: {
        ...defaultAuthenticationData,
        username: '',
        password: '',
        additional_ca_certs: lausdPresetCertificates,
      },
    },
    ssid: 'LAUSD',
    hidden: false,
  },
  CUSTOM: {
    presetLAUSDPassword: false,
    preset: WifiFormPreset.CUSTOM,
    authentication: {
      type: NetworkType.WPA_PERSONAL,
      data: defaultAuthenticationData,
    },
    ssid: '',
    hidden: false,
  },
};

const FormInput = ({
  fields,
  formData,
  address,
  handleChange,
  displayName,
  isPassword,
  placeholder,
  ...rest
}: {
  fields: Record<string, FieldMode>;
  formData: WifiFormData;
  address: string;
  handleChange: (value: string, id?: string) => void;
  displayName: string;
  isPassword?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) => {
  const Component = isPassword ? PasswordInput : Input;

  return (
    fields[address] && (
      <div className={styles.field}>
        <Component
          onChange={(value) => handleChange(value, address)}
          label={`${displayName}${
            fields[address] === FieldMode.REQUIRED ? ' *' : ''
          }`}
          type={isPassword ? 'password' : 'text'}
          placeholder={placeholder || displayName}
          required={fields[address] === FieldMode.REQUIRED}
          controlled
          controlledValue={get(formData, address)}
          {...rest}
        />
      </div>
    )
  );
};

const FormDropdown = ({
  fields,
  formData,
  address,
  handleChange,
  displayName,
  options,
}: {
  fields: Record<string, FieldMode>;
  formData: WifiFormData;
  address: string;
  handleChange: (value: string, id?: string) => void;
  displayName: string;
  options: { text: string; value: string }[];
}) => {
  return (
    fields[address] && (
      <div className={styles.field}>
        <DropdownSelect
          value={get(formData, address)}
          onChange={(value) => handleChange(value, address)}
          options={options}
          label={displayName}
          basicStyle
        />
      </div>
    )
  );
};

const UploadedFile = ({
  filename,
  onRemove,
}: {
  filename: string;
  onRemove: () => void;
}) => (
  <div className={styles.uploadedFile}>
    {isFileFormatSupported(filename) ? (
      <span className={styles.uploadFilename}>
        {filename}
        <TickIcon className={styles.tickMark} />
      </span>
    ) : (
      <span className={styles.errorText}>
        File format not supported: {filename}
      </span>
    )}
    <button type="button" className={styles.removeButton} onClick={onRemove}>
      {wifiPageRemoveButtonLabel}
    </button>
  </div>
);

const FormUpload = ({
  fields,
  formData,
  address,
  uploadErrors,
  uploadWarnings,
  addUploadWarning,
  dismissUploadWarning,
  handleChange,
  displayName,
  multiFile,
}: {
  fields: Record<string, FieldMode>;
  formData: WifiFormData;
  address: string;
  uploadErrors: Record<string, string>;
  uploadWarnings: Record<string, string[]>;
  addUploadWarning: (fieldName: string, message: string) => void;
  dismissUploadWarning: (fieldName: string, message: string) => void;
  handleChange: (value: JSONFile[], id?: string) => void;
  displayName: string;
  multiFile?: boolean;
}) => {
  const uploadedFiles: JSONFile[] = get(formData, address);

  const handleDropAccepted = async (files: File[]) => {
    if (files.length === 0) {
      addUploadWarning(address, wifiFormInvalidFileFormatWarning);
      return;
    }
    if (!multiFile && files.length > 1) {
      addUploadWarning(address, wifiFormNeedsSingleFile);
      return;
    }

    const fileReaderResults: (FileWithContent | null)[] = await Promise.all(
      files.map((f: File) => readFile(f).catch(() => null))
    );

    const hasValidTextHeader = (f: FileWithContent) =>
      // .crt files can be binary but we only accept the text type,
      // so check that content has text cert header
      f.content.startsWith('-----');

    const isDuplicateFile = (f: FileWithContent) =>
      uploadedFiles.some((uf: JSONFile) => uf.filename === f.name);

    const isGood = (f: FileWithContent | null): boolean =>
      f !== null &&
      isFileFormatSupported(f.name) &&
      hasValidTextHeader(f) &&
      !isDuplicateFile(f);

    const goodFiles: JSONFile[] = (fileReaderResults.filter(
      isGood
    ) as FileWithContent[]).map((f: FileWithContent) => ({
      filename: f.name,
      folder: f.webkitRelativePath,
      content: f.content,
    }));

    handleChange([...uploadedFiles, ...goodFiles], address);

    // check the files again and set warnings as they would be
    // cleared by handleChange
    fileReaderResults.forEach((f: FileWithContent | null) => {
      if (f === null) {
        addUploadWarning(address, wifiFormFileReadError);
        return;
      }

      if (!isFileFormatSupported(f.name)) {
        addUploadWarning(address, wifiFormInvalidFileFormatWarning);
      }

      if (uploadedFiles.some((uf: JSONFile) => uf.filename === f.name)) {
        addUploadWarning(address, wifiFormDuplicateFilenameWarning);
      }

      if (!hasValidTextHeader(f)) {
        addUploadWarning(address, wifiFormMustBePEM);
      }
    });
  };

  const handleDropRejected = (_files: File[]) => {
    addUploadWarning(address, wifiFormInvalidFileFormatWarning);
  };

  // At the time of writing, we are using react-dropzone v10.2.2
  // Newer versions of react-dropzone take an object rather than a string.
  const accept = supportedFormats.map((ext) => `.${ext}`).join(',');

  return (
    fields[address] && (
      <div className={styles.field}>
        <label className={styles.label} htmlFor={address}>
          {displayName}
        </label>
        {uploadedFiles.length > 0 &&
          uploadedFiles.map((uploadedFile: JSONFile) => (
            <UploadedFile
              key={uploadedFile.filename}
              filename={uploadedFile.filename}
              onRemove={() => {
                handleChange(
                  uploadedFiles.filter(
                    (file: JSONFile) => file.filename !== uploadedFile.filename
                  ),
                  address
                );
              }}
            />
          ))}
        {uploadWarnings[address] && (
          <div>
            {uploadWarnings[address].map((message) => (
              <div key={message} className={styles.uploadWarning}>
                <>
                  <span
                    className={styles.uploadWarningIcon}
                    role="img"
                    aria-label="Warning"
                  >
                    ⚠️
                  </span>
                  <span>{message}</span>
                </>
                <div
                  key={message}
                  className={styles.uploadWarningButtonWrapper}
                >
                  <button
                    type="button"
                    className={styles.uploadWarningButton}
                    onClick={() => dismissUploadWarning(address, message)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {(uploadedFiles.length === 0 || multiFile) && (
          <>
            {uploadErrors[address] && (
              <span className={styles.errorText}>{uploadErrors[address]}</span>
            )}

            <DropZone
              onDropAccepted={handleDropAccepted}
              accept={accept}
              onDropRejected={handleDropRejected}
              multiple={multiFile}
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  data-testid="dropzone-input-wrapper"
                  {...getRootProps()}
                  className={styles.dropzone}
                >
                  <input
                    data-testid="dropzone-input"
                    {...getInputProps()}
                    id={address}
                  />
                  <AddIcon className={styles.addIcon} />
                  <p>{wifiFormFileUploadInstruction}</p>
                </div>
              )}
            </DropZone>

            <span className={styles.smallText}>
              {supportedExtensionsListMessage(supportedFormatsWithDots)}
            </span>
          </>
        )}
      </div>
    )
  );
};

const visibleFields = (formData: WifiFormData): Record<string, FieldMode> =>
  formData.authentication.type === NetworkType.WPA_ENTERPRISE
    ? networkFields[NetworkType.WPA_ENTERPRISE][
        formData.authentication.data.authentication
      ]
    : networkFields[formData.authentication.type];

// This function checks for missing required files
export const validateRequiredFiles = (
  formData: WifiFormData
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const fields = visibleFields(formData);
  const fileFieldNames = Object.keys(fields).filter((fieldName) =>
    isUploadField(fieldName)
  );

  fileFieldNames.forEach((fieldName) => {
    if (
      fields[fieldName] === FieldMode.REQUIRED &&
      get(formData, fieldName).length === 0
    ) {
      errors[fieldName] = wifiFormMissingRequiredFileField;
    }
  });
  return errors;
};

export const defaultPreset = presets.BASIC;

export type Props = {
  formData: WifiFormData;
  onChange: (value: string | boolean | JSONFile[], id: string) => void;
  onChangePreset: (formData: WifiFormData) => void;
  onSubmit: () => void;
  userEmail: string;
  uploadErrors: Record<string, string>;
  uploadWarnings: Record<string, string[]>;
  addUploadWarning: (fieldName: string, message: string) => void;
  dismissUploadWarning: (fieldName: string, message: string) => void;
};

const WifiForm = ({
  formData,
  onChange,
  onChangePreset,
  onSubmit,
  userEmail,
  uploadErrors,
  uploadWarnings,
  addUploadWarning,
  dismissUploadWarning,
}: Props) => {
  const isUserLAUSD =
    userEmail.endsWith('@lausd.net') ||
    userEmail.endsWith('.lausd.net') ||
    userEmail.endsWith('@wow.wow') || // Test account
    userEmail.endsWith('@pi-top.com');

  const previousPreset = usePrevious(formData.preset);

  useEffect(() => {
    if (
      formData.preset === WifiFormPreset.LAUSD &&
      previousPreset !== WifiFormPreset.LAUSD &&
      isUserLAUSD
    ) {
      onChange(lausdPresetUsername, 'authentication.data.username');
      onChange(lausdPresetPassword, 'authentication.data.password');
      onChange(true, 'presetLAUSDPassword');
    }
  }, [formData.preset, previousPreset, isUserLAUSD, onChange]);

  const previousPresetLAUSDPassword = usePrevious(formData.presetLAUSDPassword);

  useEffect(() => {
    if (formData.presetLAUSDPassword === previousPresetLAUSDPassword) {
      return;
    }
    if (isUserLAUSD && formData.presetLAUSDPassword) {
      onChange(lausdPresetUsername, 'authentication.data.username');
      onChange(lausdPresetPassword, 'authentication.data.password');
    } else {
      onChange('', 'authentication.data.username');
      onChange('', 'authentication.data.password');
    }
  }, [
    formData.presetLAUSDPassword,
    previousPresetLAUSDPassword,
    isUserLAUSD,
    onChange,
  ]);

  const previousEnterpriseAuth = usePrevious(
    formData.authentication.data.authentication
  );

  // Ensure that LAUSD preset gets cleared when switching away from PEAP
  useEffect(() => {
    if (
      formData.presetLAUSDPassword &&
      previousEnterpriseAuth === EnterpriseAuthenticationType.PEAP &&
      formData.authentication.data.authentication !== previousEnterpriseAuth
    ) {
      onChange('', 'authentication.data.username');
      onChange('', 'authentication.data.password');
      onChange(false, 'presetLAUSDPassword');
    }
  }, [
    formData.presetLAUSDPassword,
    formData.authentication.data.authentication,
    previousEnterpriseAuth,
    onChange,
  ]);

  const handleChange = (value: string | boolean | JSONFile[], id?: string) => {
    if (id === 'preset') {
      const preset = presets[value as keyof typeof presets];
      if (preset) {
        onChangePreset(preset);
        return;
      }
    }
    if (!id) return;
    onChange(value, id);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit();
  };

  const fields = visibleFields(formData);

  const isPresetBasic = formData.preset === WifiFormPreset.BASIC;
  const isPresetLAUSD = formData.preset === WifiFormPreset.LAUSD;
  const isPeap =
    formData.authentication.data.authentication ===
    EnterpriseAuthenticationType.PEAP;

  return (
    <div className={styles.root}>
      {isPresetBasic && (
        <img
          src={updateCharacter}
          alt="wifi-config"
          className={styles.bannerImage}
        />
      )}
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.fieldSet}>
          <div className={styles.field}>
            <DropdownSelect
              value={formData.preset}
              onChange={(value) => handleChange(value, 'preset')}
              options={Object.entries(wifiFormPresetDisplayNames).map(
                ([key, value]) => ({
                  value: key,
                  text: value,
                })
              )}
              label="Preset"
              basicStyle
            />
          </div>
        </div>
        <div className={styles.fieldSet}>
          {!isPresetBasic && (
            <div className={styles.field}>
              <DropdownSelect
                value={formData.authentication.type}
                onChange={(value) => handleChange(value, 'authentication.type')}
                options={Object.entries(networkTypeDisplayNames).map(
                  ([key, value]) => ({
                    value: key,
                    text: value,
                  })
                )}
                label="Network Type"
                basicStyle
              />
            </div>
          )}
          <FormInput
            fields={fields}
            formData={formData}
            address="ssid"
            handleChange={handleChange}
            displayName="Network Name"
            placeholder="SSID"
          />
          {isPresetLAUSD && (
            <p className={styles.hint}>{lausdReminderToCheckNetworkName}</p>
          )}
          {fields.hidden && (
            <div className={styles.field}>
              <Checkbox
                onChange={(e) =>
                  handleChange(e.currentTarget.checked, 'hidden')
                }
                type="checkbox"
                checked={formData.hidden}
                label="Hidden Network"
                inlineLabel
                className={styles.checkbox}
              />
            </div>
          )}
        </div>
        <div className={styles.fieldSet}>
          <FormDropdown
            fields={fields}
            formData={formData}
            address="authentication.data.authentication"
            handleChange={handleChange}
            displayName="Enterprise Authentication"
            options={Object.entries(EnterpriseAuthenticationType).map(
              ([key, value]) => ({
                value: key,
                text: value,
              })
            )}
          />
          <FormInput
            fields={fields}
            formData={formData}
            address="authentication.data.username"
            handleChange={handleChange}
            displayName="Username"
            disabled={formData.presetLAUSDPassword}
          />

          <FormInput
            fields={fields}
            formData={formData}
            address="authentication.data.identity"
            handleChange={handleChange}
            displayName="Identity"
          />

          {!formData.presetLAUSDPassword && (
            <FormInput
              fields={fields}
              formData={formData}
              address="authentication.data.password"
              handleChange={handleChange}
              displayName="Password"
              isPassword
            />
          )}
          {isPresetLAUSD && isUserLAUSD && isPeap && (
            <div className={styles.field}>
              <Checkbox
                onChange={(e) =>
                  handleChange(e.currentTarget.checked, 'presetLAUSDPassword')
                }
                type="checkbox"
                checked={formData.presetLAUSDPassword}
                label="Use pi-top's LAUSD Credentials"
                inlineLabel
                className={styles.checkbox}
              />
            </div>
          )}
        </div>

        <div className={styles.fieldSet}>
          <FormInput
            fields={fields}
            formData={formData}
            address="authentication.data.anonymous_identity"
            handleChange={handleChange}
            displayName="Anonymous Identity"
          />
          <FormInput
            fields={fields}
            formData={formData}
            address="authentication.data.domain"
            handleChange={handleChange}
            displayName="Domain"
          />

          <FormDropdown
            fields={fields}
            formData={formData}
            address="authentication.data.inner_authentication"
            handleChange={handleChange}
            displayName="Inner Authentication"
            options={Object.entries(InnerAuthenticationType).map(
              ([key, value]) => ({
                text: value,
                value: key,
              })
            )}
          />
          <FormDropdown
            fields={fields}
            formData={formData}
            address="authentication.data.peap_version"
            handleChange={handleChange}
            displayName="PEAP Version"
            options={Object.entries(PeapVersion).map(([key, value]) => ({
              text: value,
              value: key,
            }))}
          />
        </div>
        <div className={styles.fieldSet}>
          <FormUpload
            fields={fields}
            formData={formData}
            address="authentication.data.ca_cert"
            uploadErrors={uploadErrors}
            uploadWarnings={uploadWarnings}
            addUploadWarning={addUploadWarning}
            dismissUploadWarning={dismissUploadWarning}
            handleChange={handleChange}
            displayName="CA Certificate"
          />

          <FormInput
            fields={fields}
            formData={formData}
            address="authentication.data.ca_cert_password"
            handleChange={handleChange}
            displayName="CA Certificate Password"
            isPassword
          />
        </div>

        <div className={styles.fieldSet}>
          <FormUpload
            fields={fields}
            formData={formData}
            address="authentication.data.user_cert"
            uploadErrors={uploadErrors}
            uploadWarnings={uploadWarnings}
            addUploadWarning={addUploadWarning}
            dismissUploadWarning={dismissUploadWarning}
            handleChange={handleChange}
            displayName="User Certificate *"
          />

          <FormInput
            fields={fields}
            formData={formData}
            address="authentication.data.user_cert_password"
            handleChange={handleChange}
            displayName="User Certificate Password"
            isPassword
          />
        </div>

        <div className={styles.fieldSet}>
          <FormUpload
            fields={fields}
            formData={formData}
            address="authentication.data.user_private_key"
            uploadErrors={uploadErrors}
            uploadWarnings={uploadWarnings}
            addUploadWarning={addUploadWarning}
            dismissUploadWarning={dismissUploadWarning}
            handleChange={handleChange}
            displayName="User Private Key *"
          />

          <FormInput
            fields={fields}
            formData={formData}
            address="authentication.data.user_private_key_password"
            handleChange={handleChange}
            displayName="User Private Key Password"
            isPassword
          />
        </div>

        {!isPresetBasic && (
          <div className={styles.fieldSet}>
            <FormUpload
              fields={fields}
              formData={formData}
              address="authentication.data.additional_ca_certs"
              uploadErrors={uploadErrors}
              uploadWarnings={uploadWarnings}
              addUploadWarning={addUploadWarning}
              dismissUploadWarning={dismissUploadWarning}
              handleChange={handleChange}
              displayName="Additional CA Certs"
              multiFile
            />
          </div>
        )}

        {Object.keys(uploadErrors).length > 0 && (
          <span className={styles.errorText}>
            {wifiFormInvalidDueToMissingFiles}
          </span>
        )}

        <PrimaryButton type="submit" className={styles.wifiFormButton}>
          {wifiDoneLabel}
        </PrimaryButton>
      </form>
    </div>
  );
};

export default memo(WifiForm);
